import type { LocationQuery, LocationQueryRaw } from 'vue-router';

/**
 * 体験一覧の並び順。URL に出る語彙と API に送る語彙を分けている。
 *
 * URL 側はゲストに見えるため `newest` のように読める語を使い、API 側は backend の
 * ソートキー規約（先頭の `-` が降順）に従う。おすすめ順は backend の既定値なので、
 * URL にもクエリにも載せない（D-013）。
 */
export const EXPERIENCE_SORT_TYPES = {
  recommended: 'recommended',
  newest: 'newest',
} as const;

export type ExperienceSortType = (typeof EXPERIENCE_SORT_TYPES)[keyof typeof EXPERIENCE_SORT_TYPES];

export const DEFAULT_EXPERIENCE_SORT_TYPE: ExperienceSortType = EXPERIENCE_SORT_TYPES.recommended;

/** URL の `?sort=` を並び順に解決する。未指定・未知の値はおすすめ順に倒す */
export const toExperienceSortType = (value: string): ExperienceSortType => {
  return value === EXPERIENCE_SORT_TYPES.newest ? EXPERIENCE_SORT_TYPES.newest : DEFAULT_EXPERIENCE_SORT_TYPE;
};

/** API に送るソートキー。おすすめ順は backend の既定値のため送らない */
export const toExperienceListApiSort = (sortType: ExperienceSortType): string | undefined => {
  return sortType === EXPERIENCE_SORT_TYPES.newest ? '-createdAt' : undefined;
};

/**
 * ソート切替後の URL クエリ。おすすめ順（既定）はパラメータを付けない。
 *
 * ページは必ず1に戻す。並びが変わった後の同じページ番号は別の体験を指すため、保持すると
 * ゲストが見ていた位置と無関係な場所に飛ぶ。絞り込みなど他のクエリはそのまま引き継ぐ。
 */
export const buildExperienceListSortQuery = (currentQuery: LocationQuery, sortType: ExperienceSortType): LocationQueryRaw => {
  const query = { ...currentQuery };
  delete query.page;
  if (sortType === DEFAULT_EXPERIENCE_SORT_TYPE) {
    delete query.sort;
  } else {
    query.sort = sortType;
  }
  return query;
};

/**
 * 絞り込みのやり直しでクエリを組み直す遷移に、いま選んでいる並び順を引き継ぐ。
 *
 * 絞り込みモーダルの遷移先はエリア / テーマページも含むが、いずれもソートを解釈するため
 * 面をまたいでも並び順は保たれる。おすすめ順（既定）はパラメータを付けない（D-013）。
 */
export const withExperienceListSortQuery = (nextQuery: LocationQueryRaw, currentSort: string): LocationQueryRaw => {
  const sortType = toExperienceSortType(currentSort);
  return sortType === DEFAULT_EXPERIENCE_SORT_TYPE ? nextQuery : { ...nextQuery, sort: sortType };
};
